import { useParticipantProperty } from '@daily-co/daily-react';
import { Mic as MicIcon, MicOff as MicOffIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

export const pillBadgeSx = {
  position: 'absolute',
  bottom: 4,
  left: 4,
  bgcolor: 'rgba(0,0,0,0.6)',
  color: 'common.white',
  borderRadius: '5px',
  px: 0.75,
  py: 0.25,
} as const;

export const iconOnlySx = {
  position: 'absolute',
  bottom: 4,
  left: 4,
  color: 'common.white',
} as const;

export const centeredNameSx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

export function MicIndicator({ enabled, fontSize = 14 }: { enabled: boolean; fontSize?: number }) {
  return enabled ? <MicIcon sx={{ fontSize }} /> : <MicOffIcon sx={{ fontSize }} />;
}

export function CenteredName({ name }: { name: string }) {
  return (
    <Box sx={centeredNameSx}>
      <Typography variant="body1" sx={{ color: 'common.white', fontWeight: 500 }}>
        {name}
      </Typography>
    </Box>
  );
}

interface ParticipantBadgeProps {
  sessionId: string;
  isLocal?: boolean;
  /** Pass explicitly when parent already knows video state. If omitted, reads from useParticipantProperty. */
  videoEnabled?: boolean;
  /** Pass explicitly for local participant when audio state is lifted. If omitted, reads from useParticipantProperty. */
  audioEnabled?: boolean;
}

export function ParticipantBadge({
  sessionId,
  isLocal = false,
  videoEnabled,
  audioEnabled,
}: ParticipantBadgeProps) {
  const name = useParticipantProperty(sessionId, 'user_name');
  const participantAudio = useParticipantProperty(sessionId, 'audio');
  const participantVideo = useParticipantProperty(sessionId, 'video');

  const audio = audioEnabled ?? participantAudio;
  const video = videoEnabled ?? participantVideo;
  const displayName = isLocal ? (name ? `${name} (You)` : 'You') : name || 'Guest';

  if (!video) {
    return (
      <Box sx={iconOnlySx}>
        <MicIndicator enabled={audio} />
      </Box>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={pillBadgeSx}>
      <MicIndicator enabled={audio} />
      <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1 }}>
        {displayName}
      </Typography>
    </Stack>
  );
}
